import { useCallback, useEffect, useRef, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { useRouter } from 'next/navigation';

// styles
import styles from './autocomplete.module.scss';

// types
import type { Coordinates, Suggestion } from '../../locator.types';
import type { FilterProps } from '../Filter.types';

// components
import { SuggestList } from './suggest-list';

// utils
import { searchSuggests, selectSuggest as selectSuggestFn } from './utils';
import { locatorEvent } from '../../trackingActions';
import { useTracking } from 'utils/hooks/useTracking';
import { isEmpty } from 'utils/is-empty';

type AutocompleteProps = {
  googleMapsRef: typeof window.google.maps | null;
  pinCountries: string[];
  searchPlaceholder: string;
  country: string;
  setCurrentCoords: (coords: Coordinates) => void;
  maxDistance: FilterProps['maxDistance'];
  setMaxDistance: (distance: FilterProps['maxDistance']) => void;
  setGeocoded: (geocoded: boolean) => void;
  mode: string;
  coordinates?: google.maps.LatLngLiteral;
  queryDelay?: number;
  minLength?: number;
  radius?: number | null;
};

export function Autocomplete({
  coordinates,
  queryDelay = 250,
  minLength = 3,
  googleMapsRef = null,
  pinCountries = [],
  searchPlaceholder,
  radius = null,
  country,
  setCurrentCoords,
  setMaxDistance,
  maxDistance,
  setGeocoded,
  mode,
}: Readonly<AutocompleteProps>) {
  const router = useRouter();
  const track = useTracking();
  const autocompleteService = useRef<google.maps.places.AutocompleteService | null>(null);
  const [geocoderService, setGeocoderService] = useState<google.maps.Geocoder | null>(null);
  const [userInput, setUserInput] = useState('');
  const [autoInput, setAutoInput] = useState(false);
  const [searchParam, setSearchParam] = useState(false);
  const [suggests, setSuggests] = useState<Suggestion[] | null>(null);
  const [cursor, setCursor] = useState(-1);
  const [suggestListHidden, setSuggestListHidden] = useState(true);

  const selectSuggest = useCallback(
    (suggest?: Suggestion) =>
      selectSuggestFn({
        suggest,
        userInput,
        onInputBlur,
        setAutoInput,
        suggests,
        mode,
        country,
        pinCountries,
        geocoderService,
        setCurrentCoords,
        setGeocoded,
        maxDistance,
        setMaxDistance,
        track,
        locatorEvent,
        setUserInput,
      }),
    [
      country,
      geocoderService,
      maxDistance,
      mode,
      pinCountries,
      setCurrentCoords,
      setGeocoded,
      setMaxDistance,
      suggests,
      track,
      userInput,
    ],
  );

  const processInputCallback = useDebouncedCallback(() => {
    processInput();
  }, queryDelay);

  useEffect(() => {
    const location = router?.query?.location;
    if (location) {
      setUserInput(location as string);
      setSearchParam(true);
    }
  }, [router.query]);

  /**
   * initialize services when google reference changes
   */
  useEffect(() => {
    if (!isEmpty(googleMapsRef)) {
      autocompleteService.current = new googleMapsRef.places.AutocompleteService();
      const service = new googleMapsRef.Geocoder();
      setGeocoderService(service);
    }
  }, [googleMapsRef]);

  /**
   * process input on userinput change
   */
  useEffect(() => {
    if (!autoInput && !searchParam) {
      processInputCallback();
    }

    if (searchParam && userInput && userInput.length >= minLength && geocoderService) {
      track.trackEvent(locatorEvent('Locator-Search', userInput));
      searchSuggests({
        userInput,
        setSuggests,
        minLength,
        googleMapsRef,
        coordinates,
        country,
        mode,
        radius,
        pinCountries,
        autocompleteService,
      });
    }
  }, [
    userInput,
    geocoderService,
    autoInput,
    searchParam,
    minLength,
    processInputCallback,
    track,
    googleMapsRef,
    coordinates,
    country,
    mode,
    radius,
    pinCountries,
  ]);

  useEffect(() => {
    if (searchParam && !isEmpty(suggests)) {
      selectSuggest();
      // should only trigger once the suggestion directly
      setSearchParam(false);
    }
  }, [suggests, searchParam, selectSuggest]);

  /**
   * When the user types in the searchbar save the input
   * in state. After it is saved, call the debounced method and
   * show all suggestions.
   */
  const handleOnChange = (e) => {
    setUserInput(e.target.value);
  };

  /**
   * When the input gets focused, show the suggest box
   */
  const onInputFocus = () => {
    if (userInput) {
      processInput();
    }
  };

  /**
   * When the input gets blurred, conditionally hide the suggest box
   * if we dont ignore the blur event, e.g. when we select a suggest item.
   */
  const onInputBlur = () => {
    setSuggests(null);
    setSuggestListHidden(true);
  };

  /**
   * Handle a keyDown event on the <Input> component.
   * Prevent the default action and ignore any blur events.
   * @param {Event} e - Event object.
   */
  const onHandleInputKeyDown = (e) => {
    setAutoInput(false);
    switch (e.which) {
      // Down Key (↓)
      case 40:
        if (suggests && cursor < suggests.length - 1) setCursor(cursor + 1);
        break;
      // Up Key (↑)
      case 38:
        if (cursor > 0) setCursor(cursor - 1);
        break;

      // Enter Key (↩)
      // Tab Key (⇥)
      case 13:
      case 9:
        e.preventDefault();
        if (cursor === -1) {
          searchSuggests({
            userInput,
            setSuggests,
            minLength,
            googleMapsRef,
            coordinates,
            country,
            mode,
            radius,
            pinCountries,
            autocompleteService,
          });
          track.trackEvent(locatorEvent('Locator-Search', e.target.value));
          selectSuggest();
        } else {
          track.trackEvent(locatorEvent('Locator-Search', suggests?.[cursor].mainText));
          selectSuggest(suggests?.[cursor]);
        }

        break;

      // Escape key
      case 27:
        e.preventDefault();
        break;

      default:
        break;
    }
  };

  /**
   * Show all suggests
   */
  const processInput = () => {
    if (!isEmpty(userInput)) {
      searchSuggests({
        userInput,
        setSuggests,
        minLength,
        googleMapsRef,
        coordinates,
        country,
        mode,
        radius,
        pinCountries,
        autocompleteService,
      });
      setSuggestListHidden(false);
    } else {
      setSuggestListHidden(true);
    }
  };

  return (
    <div className={styles.autoComplete}>
      <input
        className={styles.input}
        type="text"
        placeholder={searchPlaceholder}
        value={userInput}
        onChange={handleOnChange}
        onFocus={onInputFocus}
        onBlur={onInputBlur}
        onKeyDown={onHandleInputKeyDown}
      />
      <div className={styles.suggestWrapper}>
        <SuggestList
          suggests={suggests}
          isHidden={suggestListHidden}
          selectSuggest={selectSuggest}
          cursor={cursor}
        />
      </div>
    </div>
  );
}
